/**
 *@format
 */
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BsModalService } from "ngx-bootstrap/modal";
import { iif } from "rxjs";
import { of } from "rxjs/internal/observable/of";
import { catchError, debounceTime, map, switchMap, throttleTime, withLatestFrom } from "rxjs/operators";
import { BsModalAlertComponent, createAlertModalOptions } from "../../core/modal/modal-alert.component";
import { LocationsService } from "../../locations/state/locations.service";
import { ProviderService } from "../../reminders/reminder-settings/state/provider.service";
import { TemplateImagesService } from "../../upload-images/services/template-images.service";
import { UseImage } from "../../upload-images/types";
import { PreviewEditTemplateTabsComponent } from "../preview-edit-template-tabs.component";
import { PreviewEditTemplateActions } from "./preview-edit-template.actions";
import { PreviewEditTemplateSelectors } from "./preview-edit-template.selectors";
import { PreviewEditTemplateService } from "./preview-edit-template.service";
import { UserSelectors } from "../../user/state/user.selectors";

@Injectable()
export class PreviewEditTemplateEffects {
   constructor(
      private actions$: Actions,
      private userSelectors: UserSelectors,
      private previewEditTemplateSelectors: PreviewEditTemplateSelectors,
      private previewEditTemplateService: PreviewEditTemplateService,
      private locationsService: LocationsService,
      private providerService: ProviderService,
      private modalService: BsModalService,
      private templateImagesService: TemplateImagesService
   ) {}

   openPreviewEditTemplateDialog$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(PreviewEditTemplateActions.openPreviewEditTemplateDialog),
            map(
               ({ templateModel, fileModel, isOnlyPreview, testUserName, testUserId, activeIndex, canEditDefault }) => {
                  this.modalService.show(PreviewEditTemplateTabsComponent, {
                     initialState: {
                        templateModel,
                        fileModel,
                        isOnlyPreview,
                        testUserName,
                        testUserId,
                        activeIndex,
                        canEditDefault,
                     },
                     backdrop: "static",
                     class: "modal-lg-preview",
                     keyboard: false, // disable esc key
                  });
               }
            )
         ),
      { dispatch: false }
   );

   getModelContentByTemplateId$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getModel),
         switchMap((action) =>
            this.previewEditTemplateService
               .getModelContentByTemplateId(action.templateId, action.templateFileRef, action.updatedModel)
               .pipe(
                  map((response: any) =>
                     PreviewEditTemplateActions.getModelSuccess({
                        previewEditTemplate: response,
                        templateId: action.templateId,
                        selectedUserId: action.selectedUserId,
                     })
                  ),
                  catchError((error) => [
                     PreviewEditTemplateActions.displayDialogOnError({
                        error: error.error.error.reason,
                     }),

                     PreviewEditTemplateActions.getModelFail({
                        previewEditTemplate: error.error,
                     }),
                  ])
               )
         )
      )
   );

   getModelSuccess$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getModelSuccess),
         map((action: any) =>
            PreviewEditTemplateActions.preprocessModel({
               mediaType: "email",
               selectedUserId: action.selectedUserId,
               templateId: action.templateId,
               template: action.previewEditTemplate.templateDefaultContent,
            })
         )
      )
   );

   displayDialogOnError$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(PreviewEditTemplateActions.displayDialogOnError),
            map((action: any) => {
               this.modalService.show(BsModalAlertComponent, createAlertModalOptions("Error", action.error));
            })
         ),
      { dispatch: false }
   );

   preprocessModel$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.preprocessModel),
         throttleTime(200),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .preprocessModel(action.mediaType, action.template, action.templateId, action.selectedUserId)
               .pipe(
                  map((response: any) =>
                     PreviewEditTemplateActions.preprocessModelSuccess({
                        contextVariables: response.variables,
                     })
                  ),
                  catchError((error) => [
                     PreviewEditTemplateActions.preprocessModelFail(),

                     PreviewEditTemplateActions.displayDialogOnError({
                        error: error.error.error.reason,
                     }),
                  ])
               )
         )
      )
   );

   saveModelTemplateId$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveModel),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .saveTemplate(
                  action.templateId,
                  action.isConfirmed,
                  action.forRevert,
                  action.template,
                  action.userId,
                  action.lastModified
               )
               .pipe(
                  map((response: any) => {
                     if (response.status.rejectedModified) {
                        const editedFields = this.previewEditTemplateService.findEditedTemplateFields(
                           response.template,
                           action.forRevert,
                           true
                        );
                        // this.confirmChangingTemplate(template, forRevert, editedFields, data.status);
                        console.log("confirmChangingTemplate");
                     }

                     return PreviewEditTemplateActions.saveModelSuccess({
                        templateId: action.templateId,
                        lastModified: response.template.lastModified,
                     });
                  }),
                  catchError((error) =>
                     of(
                        PreviewEditTemplateActions.displayDialogOnError({
                           error: error.error.error.reason,
                        })
                     )
                  )
               )
         )
      )
   );

   saveModelSuccess$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveModelSuccess),
         withLatestFrom(this.userSelectors.selectSelectedUser()),
         switchMap(([action, selectedUser]) => [
            PreviewEditTemplateActions.getModel({
               templateId: action.templateId,
               templateFileRef: undefined,
               updatedModel: undefined,
               selectedUserId: selectedUser.userId,
            }),

            PreviewEditTemplateActions.openPreviewEditTemplateDialogFromAngularJSSuccess(),
         ])
      )
   );

   processPreviewTemplate$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.processPreviewTemplate),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .processPreviewTemplate(
                  action.templateId,
                  action.userId,
                  action.model,
                  action.templateFilename,
                  action.isConfirm
               )
               .pipe(
                  map((response: any) =>
                     PreviewEditTemplateActions.processPreviewTemplateSuccess({
                        contentResponse: response,
                     })
                  ),
                  catchError((error) =>
                     of(
                        PreviewEditTemplateActions.processPreviewTemplateFail({
                           error: error.error.error.reason,
                        })
                     )
                  )
               )
         )
      )
   );

   saveSource$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveSource),
         switchMap((action: any) =>
            this.previewEditTemplateService.saveSource(action.template, action.templateId).pipe(
               map((response: any) =>
                  PreviewEditTemplateActions.saveSourceSuccess({
                     templateId: action.templateId,
                     userId: action.userId,
                     model: action.updatedModel,
                     templateFilename: action.templateFileRef,
                     lastModified: response.lastModified,
                     isConfirm: action.isConfirm,
                  })
               ),
               catchError((error) =>
                  of(
                     PreviewEditTemplateActions.displayDialogOnError({
                        error: error.error.error.reason,
                     })
                  )
               )
            )
         )
      )
   );

   // https://medium.com/@amcdnl/dispatching-multiple-actions-from-ngrx-effects-c1447ceb6b22
   saveSourceSuccess$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveSourceSuccess),
         withLatestFrom(this.userSelectors.selectSelectedUser()),
         switchMap(([action, selectedUser]) => [
            PreviewEditTemplateActions.getModel({
               templateId: action.templateId,
               templateFileRef: undefined,
               updatedModel: undefined,
               selectedUserId: selectedUser.userId,
            }),

            PreviewEditTemplateActions.openPreviewEditTemplateDialogFromAngularJSSuccess(),
         ])
      )
   );

   validateSource$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.validateSource),
         withLatestFrom(this.userSelectors.selectSelectedUser()),
         switchMap(([action, selectedUser]) =>
            this.previewEditTemplateService
               .validateSource(action.template, selectedUser.userName, selectedUser.userId, action.fileName)
               .pipe(
                  map((response: any) =>
                     PreviewEditTemplateActions.validateSourceSuccess({
                        templateId: action.templateId,
                        userId: selectedUser.userId,
                        model: action.updatedModel,
                        templateFilename: action.fileName,
                        isConfirm: action.isConfirm,
                     })
                  ),
                  catchError((error) =>
                     of(
                        PreviewEditTemplateActions.displayDialogOnError({
                           error: error.error.error.reason,
                        })
                     )
                  )
               )
         )
      )
   );

   validateSourceSuccess$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.validateSourceSuccess),
         switchMap((action: any) => [
            PreviewEditTemplateActions.processPreviewTemplate({
               templateId: action.templateId,
               userId: action.userId,
               model: action.model,
               templateFilename: action.templateFilename,
               isConfirm: action.isConfirm,
            }),
         ]),
         catchError((error) =>
            of(
               PreviewEditTemplateActions.displayDialogOnError({
                  error: error.error.error.reason,
               })
            )
         )
      )
   );

   saveAsCustomTemplate$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveAsCustomTemplate),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .saveAsCustomTemplate(action.template, action.userId, action.model, action.source, action.saveType)
               .pipe(
                  map(
                     (response: any) => PreviewEditTemplateActions.openPreviewEditTemplateDialogFromAngularJSSuccess(),
                     PreviewEditTemplateActions.setSavingStatus({ savingStatus: false })
                  ),
                  catchError((error) =>
                     of(
                        PreviewEditTemplateActions.displayDialogOnError({
                           error: error.error.error.reason,
                        })
                     )
                  )
               )
         )
      )
   );

   deleteFile$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.deleteTmpFile),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .deleteFile(action.fileName)
               .pipe(map((response: any) => PreviewEditTemplateActions.deleteTmpFileSuccess()))
         )
      )
   );

   getTemplateParameters$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getTemplateParameters),
         switchMap((action: any) =>
            this.previewEditTemplateService.getTemplateParameters(action.userId, action.categoryType).pipe(
               map((response) =>
                  PreviewEditTemplateActions.getTemplateParametersSuccess({
                     templateParameters: response,
                  })
               )
            )
         )
      )
   );

   getLocations$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getLocations),
         switchMap((action: any) =>
            this.locationsService.getLocations(action.userId).pipe(
               map((response: any) =>
                  PreviewEditTemplateActions.getLocationsSuccess({
                     locations: response,
                  })
               )
            )
         )
      )
   );

   getLocationEntities$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getLocationEntities),
         switchMap((action: any) =>
            this.locationsService.getLocationEntities(action.userId, true).pipe(
               map((response: any) =>
                  PreviewEditTemplateActions.getLocationEntitiesSuccess({
                     locationEntities: response,
                  })
               )
            )
         )
      )
   );

   getProviders$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getProviders),
         switchMap((action: any) =>
            this.providerService.getProvidersByUserId(action.userId).pipe(
               map((response: any) =>
                  PreviewEditTemplateActions.getProvidersSuccess({
                     providers: response,
                  })
               )
            )
         )
      )
   );

   getCountOfStrategiesMappedWithTemplate$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.getCountOfStrategiesMappedWithTemplate),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .getCountOfStrategiesMappedWithTemplate(action.templateId, action.selectedUserName)
               .pipe(
                  map((response: any) =>
                     PreviewEditTemplateActions.getCountOfStrategiesMappedWithTemplateSuccess({
                        totalStrategies: response.totalStratgiesMapped,
                     })
                  )
               )
         )
      )
   );

   saveMedia$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.saveMedia),
         withLatestFrom(this.userSelectors.selectSelectedUser()),
         switchMap(([action, selectedUser]) =>
            this.previewEditTemplateService.setMedia(action.mediaDevices, action.templateId).pipe(
               map((response: any) => PreviewEditTemplateActions.saveMediaSuccess()),
               catchError((error) =>
                  of(
                     PreviewEditTemplateActions.displayDialogOnError({
                        error: error.reason,
                     })
                  )
               )
            )
         )
      )
   );

   reloadPreviewOnUpdateModel = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.templateUpdateModelVariable),
         withLatestFrom(this.previewEditTemplateSelectors.selectReloadPreviewTemplate()),
         withLatestFrom(this.previewEditTemplateSelectors.selectTemplateUpdateModel()),
         switchMap(([[action, reloadData], templateUpdateModel]) =>
            iif(
               () => action.reloadPreview === true,
               of(
                  PreviewEditTemplateActions.processPreviewTemplate({
                     templateFilename: reloadData.templateFileName,
                     templateId: reloadData.templateId,
                     userId: reloadData.userId,
                     isConfirm: reloadData.isConfirm,
                     model: templateUpdateModel.updatedModel,
                  })
               )
            )
         )
      )
   );

   updateTemplaetModelOnUseImageUploaded = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.useImageUploaded),
         switchMap((action: { imagesList: Array<UseImage>; imageUrl: string; variableName: string; reload: boolean }) =>
            this.templateImagesService.useImages(action.imagesList).pipe(
               map((response) =>
                  PreviewEditTemplateActions.templateUpdateModelVariable({
                     value: action.imageUrl,
                     variableName: action.variableName,
                     reloadPreview: action.reload,
                  })
               )
            )
         )
      )
   );

   isTemplateUsed$ = createEffect(() =>
      this.actions$.pipe(
         ofType(PreviewEditTemplateActions.isTemplateUsed),
         switchMap((action: any) =>
            this.previewEditTemplateService
               .isTemplateUsed(
                  action.templateId,
                  action.userId,
                  action.communicationsCategoryId,
                  action.updatedModel,
                  action.shouldOnlyUpdateFileRef,
                  action.mediaDevices,
                  action.isModelUpdated
               )
               .pipe(
                  map((templateState: any) =>
                     PreviewEditTemplateActions.isTemplateUsedSuccess({
                        templateState: templateState,
                     })
                  )
               )
         )
      )
   );

   stopSavingSource$ = createEffect(() =>
      this.actions$.pipe(
         ofType(
            ...[
               PreviewEditTemplateActions.saveSourceSuccess,
               PreviewEditTemplateActions.saveMediaSuccess,
               PreviewEditTemplateActions.saveModelSuccess,
               PreviewEditTemplateActions.displayDialogOnError,
            ]
         ),
         map((action: any) => PreviewEditTemplateActions.setSavingStatus({ savingStatus: false }))
      )
   );
}
